import { AuthRepository } from '../data/auth-repository';
import { TokenInfo } from '../types';
import { messageHandler } from '../message-handler';
import { checkIsDistributor } from '../auth-utils';

export interface InitializeAuthResult {
  success: boolean;
  tokenInfo: TokenInfo | null;
  isDistributor: boolean;
  reason: 'cortex_session' | 'stored_tokens' | 'no_auth';
}

/**
 * InitializeAuthUseCase - Business Logic for Auth Initialization
 * 
 * Flow (in priority order):
 * 1. Always start MessageHandler to listen for fresh tokens from parent frame
 * 2. In production: Check localStorage for cortex_session token FIRST (always prefer external token)
 *    - Handles tokens from parent frame (fresh login or persisted from previous session)
 * 3. If no cortex_session: Check persisted session in Zustand store (hot reload recovery)
 *    - Fallback for dev/qa environments or if cortex_session is not available
 * 4. If no auth found, show login page and wait for postMessage or manual login
 * 
 * This ensures:
 * - Production always uses cortex_session token if available (external source of truth)
 * - Hot reloads in dev/qa preserve authenticated state (no re-login needed)
 * - Page refreshes in production restore cortex_session from localStorage
 * - Fresh logins use postMessage flow from parent frame
 * - Dev/QA environments can use manual login
 * - Stale tokens are handled by API error middleware (401 responses)
 */
export class InitializeAuthUseCase {
  constructor(private readonly authRepository: AuthRepository) { }

  async execute(
    _isProduction: boolean,
    persistedToken?: string | null,
    persistedOperatorId?: string | null,
    persistedDistributorId?: string | null,
    persistedIsDistributor?: boolean,
    persistedAccountToken?: string | null,
    persistedAccountId?: string | null
  ): Promise<InitializeAuthResult> {
    // Always start listening for fresh tokens from parent
    messageHandler.startListening();

    // PRIORITY 1: Check sessionStorage/localStorage for cortex_session FIRST (external source of truth)
    // DEV ONLY: Also check in dev/qa for local testing with injected tokens
    {
      const cortexTokenInfo = await this.authRepository.tryGetCortexSessionAuth();

      if (cortexTokenInfo) {
        const isDistributor = checkIsDistributor(cortexTokenInfo);

        return {
          success: true,
          tokenInfo: cortexTokenInfo,
          isDistributor,
          reason: 'cortex_session'
        };
      }
    }

    // PRIORITY 2: Try to restore from persisted Zustand state (hot reload recovery for dev/qa)
    // Note: operatorId and accountId are optional - distributors may not have these
    // ProtectedRoute will handle showing operator selection if needed
    // Only require the main token - accountToken and accountId may not be present for all users
    if (persistedToken) {
      const isDistributor = persistedIsDistributor ?? false;

      return {
        success: true,
        tokenInfo: {
          appToken: persistedToken,
          accountToken: persistedAccountToken || persistedToken,
          accountId: persistedAccountId || 'unknown',
          operatorId: persistedOperatorId || 'pending',
          distributorId: persistedDistributorId || undefined,
          expiry: null, // Will be validated by API calls
        },
        isDistributor,
        reason: 'stored_tokens'
      };
    }

    // No valid auth - show login page and wait for postMessage or manual login
    return {
      success: false,
      tokenInfo: null,
      isDistributor: false,
      reason: 'no_auth'
    };
  }
}
